import { IValidationFunction, IValidReturn } from "aic";
import { eHierarchyLevel } from "../../functions/api/enums";
import { checkHierarchyTag } from "../../functions/api/hierarchy/checkHierarchyTag";

/**
 * Validation function for hierarchy tags
 *
 * @param level - The hierarchy level to validate tags for.
 * @param tagDesc - The tag description label used in error messages.
 * @param helpText - The help message to display.
 * @returns A validation function that checks a tag value and returns a validation result.
 */
export const tagCheck =
  (level: eHierarchyLevel, tagDesc: string, helpText: string): IValidationFunction =>
  async (backEndUrl, target, controller) => {
    // Short-circuit if the value hasn't changed
    if (target.options?.previousValue === target.value) {
      return { success: true, data: { valid: true, validationText: "" } };
    }
    // Return help text for empty values
    else if (target.value === "") {
      return {
        success: true,
        data: { valid: false, validationText: helpText },
      };
    }
    // Target is in known existing values
    else if (target.options?.existingValues?.includes(target.value)) {
      return {
        success: true,
        data: { valid: false, validationText: `This ${tagDesc} is already in use` },
      };
    }
    // Make API call to check tag validity and uniqueness
    else {
      const request = await checkHierarchyTag(
        backEndUrl,
        {
          hierarchyTag: target.value,
          level: level,
        },
        controller,
      );
      const ret: IValidReturn = request.success
        ? {
            success: true,
            data: {
              valid: request.data.isNew && request.data.isValid,
              validationText:
                request.data.isNew === false
                  ? `This ${tagDesc} is already in use`
                  : request.data.isValid === false
                    ? `This ${tagDesc} is not valid`
                    : "",
            },
          }
        : { success: false, errorText: helpText };
      return ret;
    }
  };
